/* global React, Icon, Card, EmptyState, PlummeI18n */
// ============================================================
// AI Rådgiver — bransjesammenligning
// ============================================================
// Fanen lover sammenligning mot bransjen. Finnes ikke bransjetallene, viser den en
// forklart tom tilstand i stedet for å vise noe annet enn det den lover.

function AiiIndustryBasis({ basis }) {
  if (!basis) return null;
  const rows = [
    { key: 'industry', value: basis.industryCode ? `${basis.industryCode} — ${basis.industryLabel || ''}`.trim().replace(/—\s*$/, '').trim() : t('adv.industry.unknown') },
    { key: 'size', value: t('adv.industry.size.' + basis.sizeBand) },
    { key: 'region', value: basis.region || t('adv.industry.wholeCountry') },
    { key: 'year', value: String(basis.year) },
    { key: 'source', value: t('adv.industry.source') },
    { key: 'peers', value: t('adv.industry.peers.' + (basis.peerBasis || 'limitedCompanies')) },
    { key: 'companies', value: basis.companiesInGroup != null ? String(basis.companiesInGroup) : t('adv.industry.unavailableShort') },
  ];
  return <Card>
    <window.AiSecHead icon="sliders" title={t('adv.industry.basisTitle')} sub={t('adv.industry.basisSub')} help={t('adv.industry.basisHelp')} />
    <div className="aii-cmpbasis" style={{ marginTop: 10 }}>
      {rows.map((row) => <div key={row.key}>
        <span className="lbl" data-help-title={t('adv.industry.basis.' + row.key)} data-help={t('adv.help.industry.basis.' + row.key)}>{t('adv.industry.basis.' + row.key)}</span><b>{row.value}</b>
      </div>)}
    </div>
  </Card>;
}

// Den tomme tilstanden sier hvorfor, ikke bare at. De fire årsakene krever ulike ting
// av brukeren — én av dem kan hen fikse selv, tre kan hen ikke.
function AiiIndustryEmpty({ basis, onNav }) {
  const reason = basis?.availability || 'noIndex';
  return <Card>
    <window.AiSecHead icon="users" title={t('adv.industry.title')} sub={t('adv.industry.sub')} help={t('adv.industry.help')} />
    <EmptyState noVisual acc="sparkle"
      title={t('adv.industry.empty.' + reason + '.title')}
      text={t('adv.industry.empty.' + reason + '.text')}
      compact />
    {reason === 'noIndustryCode' && onNav && <div style={{ marginTop: 12 }}>
      <button type="button" className="aii-move-btn" onClick={() => onNav('innstillinger')}>
        {t('adv.industry.empty.noIndustryCode.action')}<Icon name="arrowRight" size={15} />
      </button>
    </div>}
  </Card>;
}

// Selve sammenligningen. Tegnes bare når indeksen faktisk har tall — den deler
// søylekomponenten med resten av rådgiveren, men henter aldri tallene fra foretaket
// selv og kaller dem bransjesnitt.
function AiiIndustryComparison({ basis }) {
  if (!basis?.metrics?.length) return null;
  return <Card>
    <window.AiSecHead icon="users" title={t('adv.industry.title')} sub={t('adv.industry.sub')} help={t('adv.industry.help')} />
    {/* Påminnelsen står over tallene, ikke under: den endrer hvordan de skal leses. */}
    {basis.crossForm && <div className="aii-disclaimer" style={{ marginTop: 12 }}>
      <Icon name="info" size={17} />
      <p>{t('adv.industry.crossFormNote')}</p>
    </div>}
    <div style={{ marginTop: 8 }}>
      {basis.metrics.map((metric) => {
        const max = Math.max(1, metric.max || 0, Math.abs(metric.you), Math.abs(metric.industry)) * 1.1;
        const equal = metric.position === 'equal';
        const label = (value) => `${PlummeI18n.number(value, { maximumFractionDigits: 1 })}${metric.unit === 'ratio' ? '×' : ' %'}`;
        const width = (value) => Math.max(0, Math.min(100, value / max * 100)) + '%';
        return <div key={metric.key} className="aii-bench-row">
          <div className="aii-bench-head">
            <span className="aii-bench-name" data-help-title={t('adv.industry.metric.' + metric.key)} data-help={t('adv.help.industry.metric.' + metric.key)}>{t('adv.industry.metric.' + metric.key)}</span>
            {/* Skjeve nøkkeltall merkes der de står. En generell fotnote nederst ville
                ikke hindret noen i å lese «+59 % margin» som en seier. */}
            {metric.skewed && <span className="aii-bench-basis" title={t('adv.industry.skewedHelp')}>
              {t('adv.industry.skewed')}
            </span>}
            <span className={'aii-bench-verdict ' + (equal ? 'neutral' : metric.favorable ? 'over' : 'under')}>
              {t('adv.industry.' + metric.position)}
            </span>
          </div>
          {metric.skewed && <p className="aii-bench-skew">{t('adv.industry.skewedNote')}</p>}
          <div className="aii-bench-bars">
            <div className="aii-bench-bar">
              <span className="who">{t('adv.bench.you')}</span>
              <div className="aii-bench-track"><div className="aii-bench-fill you" style={{ width: width(metric.you) }} /></div>
              <span className="num">{label(metric.you)}</span>
            </div>
            <div className="aii-bench-bar">
              {/* Måletallet står på linja. Lønnsandelen er et vektet snitt fra SSB,
                  resten er medianer fra Regnskapsregisteret — de skal ikke se like ut. */}
              <span className="who muted" data-help-title={t('adv.industry.measure.' + (metric.measure || 'median'))} data-help={t('adv.industry.measure.' + (metric.measure || 'median') + '.help')}>{t('adv.industry.measure.' + (metric.measure || 'median'))}</span>
              <div className="aii-bench-track"><div className="aii-bench-fill avg" style={{ width: width(metric.industry) }} /></div>
              <span className="num">{label(metric.industry)}</span>
            </div>
          </div>
        </div>;
      })}
    </div>
    <p className="t-xs muted" style={{ marginTop: 12 }}>
      {t('adv.industry.sourceNote')}
      {' ' + t('adv.industry.periodNote')}
      {basis.updatedAt ? ' ' + t('adv.industry.updated', { date: PlummeI18n.date(basis.updatedAt, { day: 'numeric', month: 'long', year: 'numeric' }) }) : ''}
    </p>
  </Card>;
}

Object.assign(window, { AiiIndustryBasis, AiiIndustryEmpty, AiiIndustryComparison });
